import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EditIcon, TrashIcon } from '@/icons';
import { StaticDataType } from '@/services/staticDataType/staticDataType.types';
import { Link } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { ManageStaticDataTypeContainer } from '../ManageStaticDataTypeContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export function getColumns(
  t: (key: string) => string,
  onOpen: OnOpenProps,
  openConfirmation: OpenConfirmationProps,
  canView: boolean,
  mutateAsync: (payload: { staticDataTypeId: string }) => Promise<unknown>,
): ColumnDef<StaticDataType>[] {
  return [
    {
      accessorKey: 'sno',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'staticDataTypeName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('logsManagement.staticDataType.page.table.staticDataType')} />
      ),
      cell: ({ row }) =>
        canView ? (
          <Link
            className="font-medium text-caption text-link underline"
            to="/logs-management/static-data-type/$staticDataTypeName"
            params={{
              staticDataTypeName: row.original.staticDataTypeName,
            }}
            search={{
              staticDataTypeId: row.original.staticDataTypeId,
            }}
          >
            {row.getValue('staticDataTypeName')}
          </Link>
        ) : (
          <div>{row.getValue('staticDataTypeName')}</div>
        ),
      enableHiding: false,
    },
    {
      accessorKey: 'staticDataTypeDescription',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('logsManagement.staticDataType.page.table.staticDataDescription')}
        />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-caption text-primary">{row.getValue('staticDataTypeDescription')}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      cell: ({ row }) => {
        const id = row.original.staticDataTypeId;
        return (
          <div className="flex gap-4 items-center">
            <Can rule={{ allOf: [PERMS.static.dataType.viewDetail, PERMS.static.dataType.delete] }}>
              <EditIcon
                size={16}
                className="text-foreground-caption hover:text-foreground-detail hover:cursor-pointer"
                onClick={() => {
                  onOpen({
                    title: 'logsManagement.staticDataType.manageStaticDataType.title',
                    okText: t('common.page.modal.submit'),
                    size: 'lg',
                    modalTitle: 'logsManagement.staticDataType.manageStaticDataType.title',
                    component: <ManageStaticDataTypeContainer staticDataTypeId={id} />,
                  });
                }}
              />
            </Can>
            <Can rule={{ allOf: [PERMS.static.dataType.delete] }}>
              <TrashIcon
                className="text-destructive-text size-4 hover:cursor-pointer"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description: t('logsManagement.staticDataType.deleteStaticdataType.description'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    size: 'lg',
                    onAction: async () => {
                      await mutateAsync({ staticDataTypeId: id });
                    },
                    onCancel: () => {},
                    variant: 'destructive',
                  });
                }}
              />
            </Can>
          </div>
        );
      },
      enableHiding: false,
      enableSorting: false,
    },
  ];
}
